<template>
    <div>
        <h3>{{ __(`tools.emails.category.${category}`) }}</h3>
        <DTable
            :items="items"
            :fields="fields"
            hover
            class="sample-table table-responsive"
        >
            <template #has_addition="{item}">
                <td class="has-additions-column">
                    <DIcon
                        v-if="item.has_addition === false"
                        v-c-tooltip="__('tools.emails.missing_additions')"
                        :name="'alert'"
                    />
                </td>
            </template>
            <template #name="{item}">
                <td class="name-column">
                    {{ getName(item) }} <br>
                    <span class="text-muted fst-italic">
                        {{ getDescription(item) }}
                    </span>
                </td>
            </template>
            <template #sender="{item}">
                <td class="text-left sender-column">
                    {{ item.sender }}
                </td>
            </template>
            <template #samples="{item}">
                <td class="samples-column">
                    <div class="actions">
                        <template
                            v-if="item.samples.length > 0"
                        >
                            <span class="me-2">{{ __('tools.emails.table.use_messenger_app') }}</span>
                            <DSwitch
                                color="success"
                                variant="3d"
                                :checked="item.usesMessenger"
                                :disabled="isInLoadingState"
                                @update:checked="(value) => handleMailerChange(item, value)"
                            />
                            <DDropdown
                                id="select_sample"
                                :key="item.usesMessenger"
                                :disabled="item.usesMessenger"
                                :toggler-text="__('tools.emails.table.select-sample')"
                                color="secondary"
                                class="ms-2"
                            >
                                <DDropdownItem
                                    v-for="(sample, i) in item.samples"
                                    :id="`sample_${sample}`"
                                    :key="i"
                                    @click="openEmailSampleModal(item.type, sample)"
                                >
                                    {{ __(`tools.emails.samples.${item.type}.${sample}`) }}
                                </DDropdownItem>
                            </DDropdown>
                            <DButton
                                v-if="userPermissions.includes('tool.emails_edit') && !item.usesMessenger"
                                icon="pencil"
                                mini
                                class="ms-2"
                                @click="editSample(item)"
                            />
                            <DTooltip
                                v-if="userPermissions.includes('tool.emails_edit') && item.usesMessenger"
                                :content="editTooltipMessage(item)"
                                placement="left"
                            >
                                <template #default="{ id, on }">
                                    <DButton
                                        v-if="userPermissions.includes('tool.emails_edit') && item.usesMessenger"
                                        :aria-describedby="id"
                                        icon="pencil"
                                        :color="isMessengerMailerWithoutPlaceholders(item) ? 'danger' : undefined"
                                        mini
                                        class="ms-2"
                                        v-on="on"
                                        @click="editMessengerTemplate(item.type)"
                                    />
                                </template>
                            </DTooltip>
                        </template>
                    </div>
                </td>
            </template>
        </DTable>
        <MessengerAppTemplateConfigModal
            :show="activeRow !== null"
            @configure="goToMessengerTemplateConfig"
            @close="activeRow = null"
        />
    </div>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { router, usePage } from '@inertiajs/vue3'
import { computed, ref, nextTick } from 'vue'
import axios from 'axios'
import MessengerAppTemplateConfigModal from '~@/Pages/Tools/Emails/MessengerApp/MessengerAppTemplateConfigModal.vue'

defineProps({
    category: {
        type: String,
        default: () => {
            return ''
        }
    },
    items: {
        type: Array,
        default: () => {
            return []
        }
    }
})
const emit = defineEmits(['OpenEmailSample'])

const fields = [
    { key: 'has_addition', label: '' },
    { key: 'name', label: __('tools.emails.table.name') },
    { key: 'sender', label: __('tools.emails.table.sender') },
    { key: 'samples', label: __('tools.emails.table.samples'), sorter: false, filter: false }
]

const userPermissions = computed(() => usePage().props.auth.permissions)

const activeRow = ref(null)

function editSample (item) {
    router.get(route('tools.emails.editor.index', { emailType: item.type }))
}
function editMessengerTemplate (item) {
    router.get(route('tools.emails.messenger-app.edit', { template_name: item }))
}
function goToMessengerTemplateConfig () {
    router.get(route('tools.emails.messenger-app.edit', { template_name: activeRow.value.type }))
    activeRow.value = null
}
function getDescription (item) {
    const key = `tools.emails.email.${item.type}.description`
    const trans = __(key)

    return trans === key ? '' : trans
}
function getName (item) {
    const key = `tools.emails.email.${item.type}.name`
    const trans = __(key)

    return trans === key ? item.type : trans
}
function openEmailSampleModal (document, sample) {
    emit('OpenEmailSample', { document, sample })
}

const handleMailerChange = function (rowItem, value) {
    if (value === true && rowItem.hasMessengerAppPlaceholders === false) {
        activeRow.value = rowItem
        rowItem.usesMessenger = true

        nextTick(() => {
            rowItem.usesMessenger = false
        })
    } else {
        rowItem.usesMessenger = value
        updateMailer(rowItem, value)
    }
}

const updateMailer = function (rowItem, value) {
    axios.post(route('tools.emails.switch-to-messenger'), {
        template_name: rowItem.type,
        use_messenger_app: value
    })
        .then((response) => {
            usePage().props.flash.message = response.data.message
        })
        .catch((error) => {
            rowItem.usesMessenger = !value
            usePage().props.flash.errorMessages = error.response.data.errorMessage
        })
}
const editTooltipMessage = function (item) {
    return isMessengerMailerWithoutPlaceholders(item)
        ? __('tools.emails.messenger_placeholders_missing')
        : ''
}

const isMessengerMailerWithoutPlaceholders = function (item) {
    return item.usesMessenger && !item.hasMessengerAppPlaceholders
}
</script>

<style>
.table-responsive {
    overflow: inherit !important;
}
.samples-column div.actions div.btn-group div.dropdown-menu.show {
    width: max-content !important;
}
.samples-column div.actions div.btn-group div.dropdown-menu.show div.d-dropdown-item {
    max-width: 370px !important;
}
</style>
<style scoped>
.sample-table tr td:nth-child(1),
.sample-table thead tr th:nth-child(1) {
    max-width: 2rem;
    width: 2rem;
}

.sample-table thead tr th:nth-child(n+3):nth-child(-n+4) {
    text-align: center;
}

.actions{
    display: flex;
    justify-content: flex-start;
    align-items: center;
}

.has-additions-column {
    color: var(--colorDanger);
    width: 3%;
}

.name-column {
    width: 65%;
}

.sender-column {
    width: 10%;
}

.samples-column {
    width: 22%;
}
</style>
